import React from "react";

import { cn } from "@/lib/utils";

/**
 * Themed backdrop for the public auth screens.
 *
 * Replaces the flat pattern-boxit2.png, which was a light-only raster and stayed
 * white in dark mode. Everything here is drawn from brand tokens, so both themes
 * come for free and it stays sharp at any resolution.
 *
 * Layers, bottom to top: base surface -> hexagon lattice (nods to the locker
 * doors and the logo mark) -> two drifting brand-coloured glows -> a vignette
 * that keeps the centre calm enough for text to sit on.
 */
const AuthBackdrop = ({ className, children }) => (
  <div
    className={cn(
      "relative isolate min-h-screen w-full overflow-hidden bg-background",
      className
    )}
  >
    {/* Hexagon lattice. currentColor + opacity so it reads in both themes. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 text-primary opacity-[0.13] dark:opacity-[0.09]"
      style={{
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="97" viewBox="0 0 56 97"><path d="M28 0L56 16.17v32.33L28 64.66 0 48.5V16.17L28 0zm0 64.66L56 80.83v32.33L28 129.3 0 113.16V80.83L28 64.66z" fill="none" stroke="currentColor" stroke-width="1.25"/></svg>`
        )}")`,
        backgroundSize: "56px 97px",
      }}
    />

    {/* Brand glows. Blurred blobs rather than a flat gradient, so the colour
        shifts across the panel instead of banding. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -left-40 -top-40 h-[38rem] w-[38rem] rounded-full bg-primary/25 blur-3xl dark:bg-primary/20 motion-safe:animate-[drift_18s_ease-in-out_infinite]"
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-52 -right-32 h-[34rem] w-[34rem] rounded-full bg-secondary/20 blur-3xl dark:bg-secondary/15 motion-safe:animate-[drift_22s_ease-in-out_infinite_reverse]"
    />

    {/* Vignette: fades the pattern out behind the card so copy stays readable. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--background)/0.82)_0%,hsl(var(--background)/0.45)_45%,transparent_75%)]"
    />

    <div className="relative z-10">{children}</div>
  </div>
);

export default AuthBackdrop;
